/**
 * Grid/pose math shared across the generation core.
 *
 * Conventions (must match the renderer):
 * - Cells are 1×1, centered on integer (x, z); cell (i, k) spans
 *   x ∈ [i−0.5, i+0.5], z ∈ [k−0.5, k+0.5].
 * - Dir: 0=+x, 1=+z, 2=−x, 3=−z.
 * - A placement rotation r (quarter turns, rendered as rotation.y = r·π/2)
 *   maps a local dir d to world dir (d − r + 4) % 4, and a local point
 *   (x, z) to rotP(x, z, r).
 * - Piece defs are authored in "entry frame": entry port at (0, ·, −0.5)
 *   traveling +z (dir 1), entry cell at (0, 0), y = 0 at entry base height.
 */
import type { CurveSeg, Dir, Vec3 } from "../shared/types.ts";

export const DIRS: readonly Dir[] = [0, 1, 2, 3];

/** Unit step for a dir, as [dx, dz]. */
export const DIR_STEP: readonly (readonly [number, number])[] = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
];

export function turnLeft(d: Dir): Dir {
  return ((d + 1) % 4) as Dir;
}
export function turnRight(d: Dir): Dir {
  return ((d + 3) % 4) as Dir;
}
export function opposite(d: Dir): Dir {
  return ((d + 2) % 4) as Dir;
}

/** Move label for a cell entered heading `din` and left heading `dout`. */
export function moveLabel(din: Dir, dout: Dir): "F" | "L" | "R" {
  if (din === dout) return "F";
  if (dout === turnLeft(din)) return "L";
  if (dout === turnRight(din)) return "R";
  throw new Error(`impossible move ${din}→${dout}`);
}

/** Rotate a local dir by r quarter turns (piece rotation convention). */
export function rotDir(d: Dir, r: Dir): Dir {
  return ((d - r + 4) % 4) as Dir;
}

/** Rotate a point in the xz plane by r quarter turns (matches rotation.y = r·π/2). */
export function rotP(x: number, z: number, r: Dir): [number, number] {
  switch (r) {
    case 0:
      return [x, z];
    case 1:
      return [z, -x];
    case 2:
      return [-x, -z];
    case 3:
      return [-z, x];
  }
}

export function rotVec3(v: Vec3, r: Dir): Vec3 {
  const [x, z] = rotP(v[0], v[2], r);
  return [x, v[1], z];
}

/**
 * Rigid transform of entry-frame content into world:
 * rotate by r, then translate by (tx, ty, tz).
 */
export interface Xform {
  r: Dir;
  tx: number;
  ty: number;
  tz: number;
}

export function applyXform(m: Xform, v: Vec3): Vec3 {
  const [x, z] = rotP(v[0], v[2], m.r);
  return [x + m.tx, v[1] + m.ty, z + m.tz];
}

/**
 * Xform placing a def's entry frame so the entry cell lands on world cell
 * (cx, cz), heading `dir`, with entry base height `baseY`.
 * Canonical entry heading is +z (dir 1): r = (1 − dir + 4) % 4.
 */
export function windowXform(
  cx: number,
  cz: number,
  dir: Dir,
  baseY: number,
): Xform {
  return { r: ((1 - dir + 4) % 4) as Dir, tx: cx, ty: baseY, tz: cz };
}

export function xformSeg(m: Xform, seg: CurveSeg): CurveSeg {
  switch (seg.kind) {
    case "line":
      return { kind: "line", a: applyXform(m, seg.a), b: applyXform(m, seg.b) };
    case "cubic":
      return {
        kind: "cubic",
        p: [
          applyXform(m, seg.p[0]),
          applyXform(m, seg.p[1]),
          applyXform(m, seg.p[2]),
          applyXform(m, seg.p[3]),
        ],
      };
    case "arc": {
      const c = applyXform(m, seg.center);
      // Rotation by r quarter turns maps angle θ → θ − r·π/2 in our
      // convention (point = center + radius·(cos θ, ·, sin θ), and
      // rotP(cos θ, sin θ, 1) = (sin θ, −cos θ) = (cos(θ−π/2), sin(θ−π/2))).
      const shift = -m.r * Math.PI / 2;
      return {
        kind: "arc",
        center: c,
        radius: seg.radius,
        a0: seg.a0 + shift,
        a1: seg.a1 + shift,
        y0: seg.y0 + m.ty,
        y1: seg.y1 + m.ty,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Curve segment evaluation
// ---------------------------------------------------------------------------

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function segPoint(seg: CurveSeg, t: number): Vec3 {
  switch (seg.kind) {
    case "line":
      return [
        lerp(seg.a[0], seg.b[0], t),
        lerp(seg.a[1], seg.b[1], t),
        lerp(seg.a[2], seg.b[2], t),
      ];
    case "arc": {
      const th = lerp(seg.a0, seg.a1, t);
      return [
        seg.center[0] + seg.radius * Math.cos(th),
        lerp(seg.y0, seg.y1, t),
        seg.center[2] + seg.radius * Math.sin(th),
      ];
    }
    case "cubic": {
      const [p0, p1, p2, p3] = seg.p;
      const u = 1 - t;
      const out: number[] = [];
      for (let i = 0; i < 3; i++) {
        out.push(
          u * u * u * p0[i] + 3 * u * u * t * p1[i] + 3 * u * t * t * p2[i] +
            t * t * t * p3[i],
        );
      }
      return out as unknown as Vec3;
    }
  }
}

export function segLength(seg: CurveSeg): number {
  switch (seg.kind) {
    case "line": {
      const dx = seg.b[0] - seg.a[0];
      const dy = seg.b[1] - seg.a[1];
      const dz = seg.b[2] - seg.a[2];
      return Math.hypot(dx, dy, dz);
    }
    case "arc": {
      const horiz = Math.abs(seg.a1 - seg.a0) * seg.radius;
      return Math.hypot(horiz, seg.y1 - seg.y0);
    }
    case "cubic": {
      // Numeric: 16-step polyline is plenty at our scales.
      let len = 0;
      let prev = segPoint(seg, 0);
      for (let i = 1; i <= 16; i++) {
        const p = segPoint(seg, i / 16);
        len += Math.hypot(p[0] - prev[0], p[1] - prev[1], p[2] - prev[2]);
        prev = p;
      }
      return len;
    }
  }
}

/** Reverse a segment's direction of travel. */
export function reverseSeg(seg: CurveSeg): CurveSeg {
  switch (seg.kind) {
    case "line":
      return { kind: "line", a: seg.b, b: seg.a };
    case "arc":
      return { ...seg, a0: seg.a1, a1: seg.a0, y0: seg.y1, y1: seg.y0 };
    case "cubic":
      return { kind: "cubic", p: [seg.p[3], seg.p[2], seg.p[1], seg.p[0]] };
  }
}
